import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { Link } from '@hologram-dimension/link';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { Popover } from '@hologram-dimension/popover';
import useDevice from 'api/legacy/devices/useDevice';
import ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import useSimBilling from 'api/sims/useSimBilling';
import useSimDetails from 'api/sims/useSimDetails';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import useSimStatuses from 'api/sims/useSimStatuses';
import BillingPeriodField from 'components/BillingPeriodField/BillingPeriodField';
import Field from 'components/Field/Field';
import useChangePlan from 'components/ManageSimButton/ManageSimItems/Items/useChangePlan';
import SimStatusChip from 'components/SimStatusChip/SimStatusChip';
import useUserInfo from 'hooks/User/useUserInfo';
import { useCallback, useMemo, type FC } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import useLegacyDashboard from 'state/contexts/LegacyDashboard/useLegacyDashboard';
import { EnumsSimNetworkState } from 'types/__generated__/SimInventoryApi';
import type { OnSimsModified, OpenModalCallback } from 'types/common';
import getUserFriendlyBytes from 'utils/bytes/getUserFriendlyBytes';
import apiDateToDateTime from 'utils/dates/apiDateToDateTime';
import formatDate from 'utils/dates/formatDate';
import useSimUrl from 'utils/hooks/useSimUrl';
import EnumSimPage from 'utils/legacy/paths/simPage';
import formatMoney from 'utils/numbers/formatMoney';
import PanelSection from 'components/PanelSection/PanelSection';
import clsx from 'clsx';
import useUrl from 'utils/hooks/useUrl';
import styles from './SimPlanPanel.module.css';

interface SimPlanPanelProps {
  hologramSimId?: HologramSimId;
  orgId?: OrgId;
  handleModalOpen: OpenModalCallback;
  onSimsModified: OnSimsModified;
  simDetailBaseUrl: string;
  dataPlanUrl: string;
}

const SimPlanPanel: FC<SimPlanPanelProps> = ({
  hologramSimId,
  orgId,
  handleModalOpen,
  onSimsModified,
  simDetailBaseUrl,
  dataPlanUrl,
}) => {
  const {
    simBilling,
    error: simBillingError,
    isLoading: isSimBillingLoading,
    isValidating: isSimBillingValidating,
  } = useSimBilling(hologramSimId);
  const {
    simDetails,
    error: simDetailsError,
    isLoading: isSimDetailsLoading,
    isValidating: isSimDetailsValidating,
  } = useSimDetails(hologramSimId);
  const {
    deviceDetails,
    error: simDeviceDetailsError,
    isLoading: isSimDeviceDetailsLoading,
    isValidating: isSimDeviceDetailsValidating,
  } = useSimDeviceDetails(hologramSimId);
  const { claimedDeviceId } = deviceDetails;
  const {
    networkStatusGroups,
    error: simStatusesError,
    isLoading: isSimStatusesLoading,
    isValidating: isSimStatusesValidating,
  } = useSimStatuses();
  const {
    device: legacyDeviceDetails,
    error: deviceError,
    isLoading: isDeviceLoading,
    isValidating: isDeviceValidating,
  } = useDevice(claimedDeviceId, orgId);
  const {
    planId,
    planName,
    planType,
    planZone,
    smsCharge,
    smsLimit,
    overageCharge,
    dataAlertLimit,
    dataUsageLimit,
    dataIncluded,
    poolDataLimit,
    simRenewalDate,
    platformFee,
    dataRate,
    hasVariableRates,
    zeroDataRate,
    hasZeroDataRate,
  } = simBilling;
  const {
    phoneNumber,
    expectedActivationTime,
    activationTime,
    preflightUsage,
    billingPeriodUsage,
  } = simDetails;
  const usage = activationTime ? (billingPeriodUsage ?? 0) : (preflightUsage ?? 0);
  const { phonenumber_cost: phoneNumberCost } = legacyDeviceDetails;

  const onSetUsageAlertModalOpen = useCallback(() => {
    handleModalOpen({
      modalName: 'SetUsageAlertsModal',
      modalProps: {
        sims: new Map([[hologramSimId, claimedDeviceId]]),
        onModalSuccess: onSimsModified,
      },
    });
  }, [handleModalOpen, hologramSimId, claimedDeviceId, onSimsModified]);

  const { networkStatus } = simDetails;
  const isPredeployment = networkStatusGroups?.predeployment?.includes(
    networkStatus as EnumsSimNetworkState
  );
  const isFactory = networkStatus?.startsWith('FACTORY') || networkStatus?.startsWith('INACTIVE');

  const error =
    simDetailsError || simBillingError || simDeviceDetailsError || simStatusesError || deviceError;
  const isLoading =
    isSimBillingLoading ||
    isSimDetailsLoading ||
    isSimDeviceDetailsLoading ||
    isSimStatusesLoading ||
    isDeviceLoading;
  const isValidating =
    isSimBillingValidating ||
    isSimDetailsValidating ||
    isSimDeviceDetailsValidating ||
    isSimStatusesValidating ||
    isDeviceValidating;

  const total =
    (usage === 0 && hasZeroDataRate ? zeroDataRate! : (platformFee ?? 0)) +
    parseFloat(phoneNumberCost ?? '0');

  const isPayAsYouGo = planType === 'Pay as you go';

  // TOOD: Clean up the shared logic for determining the change plan button's state
  const changePlanItem = useChangePlan({
    context: 'details',
    selectedSims: new Map([[hologramSimId!, claimedDeviceId]]),
    disabled: false,
  } as any);

  const { releaseFlags } = useUserInfo();

  const hasDataUsageAlerts = releaseFlags.has(ReleaseFlag.FEATURE_DATA_USAGE_ALERTS);

  const { sendAnalyticsEvent } = useAnalytics();

  const { setDataUsageLimits } = useLegacyDashboard() ?? {};
  const handleDataLimitClick = () => {
    if (!setDataUsageLimits) {
      throw new Error('Tried to set data usage limit outside of legacy dashboard context.');
    }

    if (claimedDeviceId) {
      setDataUsageLimits([claimedDeviceId]);
    }
  };

  const coverageUrl = useSimUrl({
    simDetailBaseUrl,
    orgId,
    simId: hologramSimId,
    subpage: EnumSimPage.coverage,
  });

  const hydratedDataPlanUrl = useUrl({
    url: dataPlanUrl,
    replacers: [
      { placeholder: 'orgid', value: orgId?.toString() ?? '' },
      { placeholder: 'dataPlanId', value: planId?.toString() ?? '' },
      { placeholder: 'dataPlanZone', value: planZone?.toString() ?? '' },
    ],
  });

  const planRate = useMemo(() => {
    if (hasVariableRates) {
      return (
        <Link to={coverageUrl!} type="inline">
          Varies by network
        </Link>
      );
    }
    return isPayAsYouGo ? (
      <Link to={coverageUrl!} type="inline">
        {formatMoney(dataRate)}
      </Link>
    ) : (
      formatMoney(overageCharge)
    );
  }, [hasVariableRates, isPayAsYouGo, coverageUrl, dataRate, overageCharge]);

  const includedData = useMemo(() => {
    if (dataIncluded) {
      return getUserFriendlyBytes(dataIncluded, 'short');
    }
    if (poolDataLimit) {
      return `${getUserFriendlyBytes(poolDataLimit, 'short')} data pool`;
    }
    return planType;
  }, [dataIncluded, poolDataLimit, planType]);

  let panelContents;
  if (error) {
    panelContents = (
      <PanelNotification
        variant="error"
        label="We ran into an issue loading the SIM data plan"
        actions={
          <Button
            variant="secondary"
            iconStart="Sync"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </Button>
        }
      >
        We will keep trying in the background or you can try to reload the page.
      </PanelNotification>
    );
  } else if (isLoading) {
    panelContents = <PanelNotification>Loading data plan...</PanelNotification>;
  } else if (isFactory) {
    panelContents = <PanelNotification>No data plan has been added to this SIM.</PanelNotification>;
  } else {
    panelContents = (
      <>
        <PanelSection
          variant="simple"
          fieldGroups={[
            <>
              <Field
                label="Name"
                value={
                  <div className={styles.popoverWithTooltip}>
                    <span className={styles.planName}>{planName}</span>
                    <Popover
                      className={styles.popover}
                      id="popover-plan-id"
                      content={
                        <span>
                          Data plan ID: {planId}
                          <br />
                          Zone: {planZone}
                        </span>
                      }
                      onOpen={() => {
                        if (sendAnalyticsEvent) sendAnalyticsEvent('Info - Data plan name');
                      }}
                    >
                      <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
                    </Popover>
                  </div>
                }
              />
              <Field label="Type" value={planType} />
            </>,
            <div className={styles.viewDetailsButton}>
              <Button to={hydratedDataPlanUrl!} variant="tertiary" iconEnd="ArrowEastShort">
                View details
              </Button>
            </div>,
          ]}
        />
        <PanelSection
          variant="simple"
          heading="Data usage"
          fieldGroups={[
            <>
              {poolDataLimit && (
                <Field
                  label="Data pool total data"
                  value={getUserFriendlyBytes(poolDataLimit, 'short')}
                />
              )}
              <Field label="Included data" value={includedData} />

              <Field
                label={isPayAsYouGo ? 'Per MB' : 'Overage per MB'}
                value={planRate}
                valueClassname={styles.numeric}
              />
              {hasDataUsageAlerts && (dataAlertLimit ?? -1) >= 0 && (
                <Field
                  label="Usage alert"
                  value={
                    <Link type="inline" onClick={onSetUsageAlertModalOpen}>
                      {`Alert at ${getUserFriendlyBytes(dataAlertLimit, 'short')}`}
                    </Link>
                  }
                />
              )}
              <Field
                label="Data limit"
                valueClassname={styles.numeric}
                value={
                  <Link type="inline" onClick={handleDataLimitClick}>
                    {dataUsageLimit && dataUsageLimit > 0
                      ? `Pause at ${getUserFriendlyBytes(dataUsageLimit, 'short')}`
                      : 'No limit'}
                  </Link>
                }
              />
            </>,
            <>
              {!isPredeployment && (
                <BillingPeriodField
                  hologramSimId={hologramSimId}
                  direction="row"
                  label="Billing period (UTC)"
                />
              )}
              {isPredeployment && <Field label="Billing period (UTC)" />}
              <Field
                label="Per outbound SMS"
                value={formatMoney(smsCharge)}
                valueClassname={styles.numeric}
              />
              <Field
                label="Outbound SMS limit"
                value={isPredeployment ? 10 : smsLimit}
                valueClassname={styles.numeric}
              />
            </>,
          ]}
        />

        <PanelSection
          variant="simple"
          heading="SIM pricing"
          fieldGroups={[
            <>
              <Field
                label="Monthly per SIM"
                value={formatMoney(platformFee)}
                valueClassname={clsx(
                  styles.numeric,
                  usage === 0 && hasZeroDataRate && styles.strikethrough
                )}
              />
              {usage === 0 && hasZeroDataRate && (
                <Field
                  label="Zero-data MRC"
                  value={formatMoney(zeroDataRate)}
                  valueClassname={styles.numeric}
                />
              )}
              {phoneNumber && (
                <Field
                  label="Phone number"
                  value={formatMoney(parseFloat(phoneNumberCost ?? '0'))}
                  valueClassname={styles.numeric}
                />
              )}
              <hr className={styles.hr} />
              {isPredeployment && (
                <Field
                  label="Monthly total"
                  value={
                    <div className={styles.predeploymentTotal}>
                      <strong>Free until activated</strong>
                      <SimStatusChip networkState={networkStatus} />
                    </div>
                  }
                  valueClassname={styles.numeric}
                />
              )}
              <Field
                label={isPredeployment ? 'Total after activation' : 'Total'}
                value={
                  <>
                    <strong>{formatMoney(total)}</strong>{' '}
                    <span className={styles.diminished}>
                      {poolDataLimit && '+ data pool overage'}
                      {!poolDataLimit && isPayAsYouGo && '+ data usage'}
                      {!poolDataLimit && !isPayAsYouGo && '+ data overage'}
                    </span>
                  </>
                }
                valueClassname={styles.numeric}
              />
            </>,
            <>
              {isPredeployment && !activationTime && expectedActivationTime && (
                <Field
                  label="Target activation date"
                  value={
                    <>
                      {formatDate(expectedActivationTime, 'long')}{' '}
                      <span className={styles.diminished}>
                        ({apiDateToDateTime(expectedActivationTime)?.toRelative({ unit: 'days' })})
                      </span>
                    </>
                  }
                  valueClassname={styles.numeric}
                />
              )}
              <Field
                label="Next renewal (UTC)"
                value={
                  simRenewalDate ? (
                    <div className={styles.popoverWithTooltip}>
                      {formatDate(simRenewalDate, 'long')}{' '}
                      <span className={styles.diminished}>
                        ({apiDateToDateTime(simRenewalDate)?.toRelative({ unit: 'days' })})
                      </span>
                      <Popover
                        className={styles.popover}
                        id="popover-renewal-date"
                        content="SIM platform fees, prepaid data, and add-on features are charged every 30 days,
                beginning on the day this SIM was activated."
                        onOpen={() => {
                          if (sendAnalyticsEvent) sendAnalyticsEvent('Info - Next renewal');
                        }}
                      >
                        <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
                      </Popover>
                    </div>
                  ) : null
                }
                valueClassname={styles.numeric}
              />
              {hasZeroDataRate && (
                <Field
                  label="Zero-data rate"
                  value={
                    <div className={styles.popoverWithTooltip}>
                      {formatMoney(zeroDataRate)} per month
                      <Popover
                        className={styles.popover}
                        id="popover-zero-data-rate"
                        content="If your SIM sends 0 bytes of data within a billing period it will be charged at the zero-data rate."
                        onOpen={() => {
                          if (sendAnalyticsEvent) sendAnalyticsEvent('Info - Zero-data rate');
                        }}
                      >
                        <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
                      </Popover>
                    </div>
                  }
                  valueClassname={styles.numeric}
                />
              )}
            </>,
          ]}
        />
      </>
    );
  }
  return (
    <Panel
      header="Data plan"
      footer={
        !isFactory && (
          <span className={styles.footer}>
            Data usage is charged at the end of each closed session. Sessions may be aggregated to
            meet the minimum charge of $0.10.
          </span>
        )
      }
      footerVariant="alternative"
      noContentPadding
      headerActions={
        !isFactory && changePlanItem ? (
          <Button
            onClick={(changePlanItem as any).onClick}
            variant="secondary"
            iconStart={undefined}
          >
            Change data plan
          </Button>
        ) : undefined
      }
      isLoading={isLoading || isValidating}
      contentWell={
        isPredeployment &&
        !isFactory &&
        'This SIM is configured for pre-deployment testing. On activation, it will activate into this data plan and its initial billing period will begin.'
      }
    >
      {panelContents}
    </Panel>
  );
};

export default SimPlanPanel;
